// Employment episode display helpers (ADR-137 / ADR-138)
//
// Extracted from the Employment Episodes tab so that tab and the ADR-138 hire-date
// picker cannot drift apart: both read the same source reference, format the same
// date range, and answer "is this episode the hire date's source?" the same way.

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { EMPLOYMENT_EVENT_TYPES } from './employmentEventTypes';
import { EVENT_VALIDATION_STATES } from './employmentValidationStates';

dayjs.extend(utc);

/**
 * ADR-138 — where an employee's hire date is taken from.
 *   'keep'    leave the current hire date alone (rehire only; on the standalone
 *             surface "keep" is just Cancel)
 *   'new'     adopt the contract start date of the rehire being entered, which has no
 *             episode row yet because that episode does not exist until submit
 *   'episode' adopt an existing episode's startDate
 */
export const HIRE_DATE_SOURCE_KIND = {
  KEEP: 'keep',
  NEW: 'new',
  EPISODE: 'episode',
};

/**
 * ADR-049 source reference on an employment episode — { source, contractId }.
 * Always an object, so callers can read `.contractId` without guarding.
 * @param {Object} [episode]
 */
export const episodeSourceRef = (episode) => episode?.payload?.sourceReference || {};

/**
 * ADR-137 contract number, or null when the episode carries no link. Stringified so
 * ingestion's numeric ids and hand-typed alphanumerics render alike (mirrors
 * `deriveContractNumber` in radar-api's FTC report service).
 * @param {Object} [episode]
 */
export const episodeContractNumber = (episode) => {
  const contractId = episodeSourceRef(episode).contractId;
  if (contractId === null || contractId === undefined || contractId === '') return null;
  return String(contractId);
};

/**
 * Stored dates carry a time component; every employment date question in this domain
 * is a calendar-day question (ADR-017), so comparisons go through this key.
 * @param {Date|string|null} [value]
 * @returns {string|null} 'YYYY-MM-DD'
 */
export const toEpisodeDayKey = (value) => (value ? dayjs.utc(value).format('YYYY-MM-DD') : null);

/** Display format used everywhere episodes are shown. */
export const formatEpisodeDate = (value) => (value ? dayjs.utc(value).format('DD/MM/YYYY') : null);

/** "01/07/2019 - Present" / "01/07/2019 - 03/08/2026" */
export const formatEpisodeRange = (episode) =>
  `${formatEpisodeDate(episode?.startDate)} - ${episode?.endDate ? formatEpisodeDate(episode.endDate) : 'Present'}`;

/** The still-open episode, if any. The guard layer allows at most one today. */
export const findOpenEpisode = (episodes) => (episodes || []).find((episode) => !episode.endDate) || null;

/**
 * ADR-155 §4 / ADR-144 §6.4 — the Pending RENEWAL_SCHEDULED on an episode, i.e.
 * the scheduled replacement that Cancel Scheduled Renewal addresses BY EVENT.
 * One definition, shared by the Contracts tab (which renders it in place) and
 * the lifecycle modal router (which puts its id on the cancel payload), so the
 * event the operator sees is the event the request names. Deliberately not
 * gated on the episode being open: in the gapped regime the intent outlives
 * the contract it was recorded on (ADR-113 §9.1).
 * @param {Object} [episode] - an episode with its employmentEvents included
 */
export const findPendingRenewalEvent = (episode) =>
  (episode?.employmentEvents || []).find(
    (event) =>
      event.eventType?.code === EMPLOYMENT_EVENT_TYPES.RENEWAL_SCHEDULED &&
      event.eventValidationState === EVENT_VALIDATION_STATES.PENDING
  ) || null;

/**
 * ADR-138: the hire date's provenance is NOT stored — it is re-derived by matching
 * the date. This is the whole read side of that decision.
 * @param {Object} episode
 * @param {Date|string|null} hireDate
 */
export const isHireDateSource = (episode, hireDate) =>
  Boolean(hireDate) && toEpisodeDayKey(episode?.startDate) === toEpisodeDayKey(hireDate);

/**
 * The episode the hire date currently comes from, or null when it matches none.
 *
 * Null is NOT an error state: employees predating the episode model, importer rows
 * and `adjust-missing-episodes` repairs legitimately sit there. ADR-138 §8.3 —
 * surface it, never block on it.
 */
export const findHireDateEpisode = (episodes, hireDate) =>
  (episodes || []).find((episode) => isHireDateSource(episode, hireDate)) || null;
